/* global logger */
/* eslint-disable class-methods-use-this */

const { PaymentStrategies, getShortageDamageUnits, Metrics } = require('pando-constants');
const { invert } = require('lodash');
const convert = require('convert-units');
const Helper = require('./utils/helper');

class CartonDamage {
  getWeightInTon(weight) {
    let materialWeight = weight.value; // weight of the material
    if (weight.unit && weight.unit.toLowerCase() === Metrics.Kg) {
      materialWeight = convert(materialWeight).from(Metrics.Kg)
        .to(getShortageDamageUnits().MT.toLowerCase()); // if the unit is kg, converting to MT
    }
    return materialWeight;
  }
  calculateChargeByCategory(material, category) {
    const type = category.carton_damage_type.toString();
    const count = material.epod.carton_damage_count;
    const cartondamageTypes = invert(PaymentStrategies.cartonDamage);
    let result = 0;
    switch (type) {
      case cartondamageTypes.cartonDamageUsingFixedValue:
        return count * category.carton_damage_factor;
      case cartondamageTypes.cartonDamageUsingFactorAndMrp:
        return count * category.carton_damage_factor * material.mrp;
      case cartondamageTypes.cartonDamageUsingInvoice:
        if (category.carton_damage_unit.toLowerCase() === getShortageDamageUnits().MT.toLowerCase()) {
          const materialWeight = this.getWeightInTon(material.weight);
          result = count * category.carton_damage_factor *
            (material.invoice.amount / materialWeight);
        } else {
          result = count * category.carton_damage_factor *
          (material.invoice.amount / material.quantity.value);
        }
        return result;
      default:
        logger.info('Unhandled carton damage type');
        return 0;
    }
  }
  cartonDamageUsingFixedValue(data, clientConfiguration) {
    const computeSdcdAtCategoryLevel = Helper.getComputeSdcdAtCategoryLevel(clientConfiguration);
    // Check for seal no.
    if (Helper.isSealIntact(data.sealNoDetails)) {
      return { cartonDamage: 0 };
    }
    const materialShortageDamageMap = {};
    const factor = data.defectsSettings.cartonDamage.factor;
    const cartonDamageCharge = data.destinations.reduce((acc, destination) =>
      acc + destination.paymentLevelMaterials.reduce((total, material) => {
        let totalCost = total;
        const locationId = destination.destinationGateId || destination.locationId;
        if(!materialShortageDamageMap[locationId]) {
          materialShortageDamageMap[locationId] = {};
        }
        const category = data.materials.category && data.materials.category[material.category];
        let calculatedCost;
        if (category && category.carton_damage_type && computeSdcdAtCategoryLevel) {
          calculatedCost = this.calculateChargeByCategory(material, category);
        } else {
          calculatedCost = material.epod.carton_damage_count * factor;
        }
        materialShortageDamageMap[locationId][material._id] = calculatedCost;
        totalCost += calculatedCost;
        return totalCost;
      }, 0), 0);
    return { cartonDamage: cartonDamageCharge.toFixed(2), materialShortageDamageMap };
  }
  cartonDamageUsingFactorAndMrp(data, clientConfiguration) {
    const computeSdcdAtCategoryLevel = Helper.getComputeSdcdAtCategoryLevel(clientConfiguration);
    // Check for seal no.
    if (Helper.isSealIntact(data.sealNoDetails)) {
      return { cartonDamage: 0 };
    }
    const factor = data.defectsSettings.cartonDamage.factor;
    const materialShortageDamageMap = {};
    const cartonDamageCharge = data.destinations.reduce((acc, destination) =>
      acc + destination.paymentLevelMaterials.reduce((total, material) => {
        let totalCost = total;
        const locationId = destination.destinationGateId || destination.locationId;
        if(!materialShortageDamageMap[locationId]) {
          materialShortageDamageMap[locationId] = {};
        }
        let calculatedCost;
        const category = data.materials.category && data.materials.category[material.category];
        if (category && category.carton_damage_type && computeSdcdAtCategoryLevel) {
          calculatedCost = this.calculateChargeByCategory(material, category);
        } else {
          calculatedCost = (material.epod.carton_damage_count * factor * material.mrp);
        }
        materialShortageDamageMap[locationId][material._id] = calculatedCost;
        totalCost += calculatedCost;
        return totalCost;
      }, 0), 0);
    return { cartonDamage: cartonDamageCharge.toFixed(2), materialShortageDamageMap };
  }
  cartonDamageUsingInvoice(data, clientConfiguration) {
    const computeSdcdAtCategoryLevel = Helper.getComputeSdcdAtCategoryLevel(clientConfiguration);
    // Check for seal no.
    if (Helper.isSealIntact(data.sealNoDetails)) {
      return { cartonDamage: 0 };
    }
    const materialShortageDamageMap = {};
    const factor = data.defectsSettings.cartonDamage.factor;
    const cartonDamageCharge = data.destinations.reduce((acc, destination) =>
      acc + destination.paymentLevelMaterials.reduce((total, material) => {
        let totalCost = total;
        const locationId = destination.destinationGateId || destination.locationId;
        if(!materialShortageDamageMap[locationId]) {
          materialShortageDamageMap[locationId] = {};
        }
        let calculatedCost;
        const category = data.materials.category && data.materials.category[material.category];
        if (category && category.carton_damage_type && computeSdcdAtCategoryLevel) {
          calculatedCost = this.calculateChargeByCategory(material, category);
        } else if (category && category.carton_damage_unit &&
          category.carton_damage_unit.toLowerCase() === getShortageDamageUnits().MT.toLowerCase()) {
          const materialWeight = this.getWeightInTon(material.weight);
          calculatedCost = (material.epod.carton_damage_count) * factor *
          (material.invoice.amount / materialWeight);
        } else {
          calculatedCost = (material.epod.carton_damage_count * factor *
          (material.invoice.amount / material.quantity.value));
        }
        materialShortageDamageMap[locationId][material._id] = calculatedCost;
        totalCost += calculatedCost;
        return totalCost;
      }, 0), 0);
    return { cartonDamage: cartonDamageCharge.toFixed(2), materialShortageDamageMap };
  }
  cartonDamageUsingAmount(data) {
    // Check for seal no.
    if (Helper.isSealIntact(data.sealNoDetails)) {
      return { cartonDamage: 0 };
    }
    const materialShortageDamageMap = {};
    const cartonDamageCharge = data.destinations.reduce((acc, destination) =>
      acc + destination.paymentLevelMaterials.reduce((total, material) => {
        const locationId = destination.destinationGateId || destination.locationId;
        if(!materialShortageDamageMap[locationId]) {
          materialShortageDamageMap[locationId] = {};
        }
        const calculatedCost = (material.epod.carton_damage_charge || 0);
        materialShortageDamageMap[locationId][material._id] = calculatedCost;
        return total + calculatedCost;
      }, 0), 0);
    return { cartonDamage: cartonDamageCharge.toFixed(2), materialShortageDamageMap };
  }
  cartonDamageCount(data) {
    const cartonCount = data.destinations.reduce((acc, destination) =>
      acc + destination.paymentLevelMaterials.reduce((each, material) =>
        each + (material.epod.carton_damage_count), 0), 0);
    return cartonCount;
  }
  getMaterialLevelCartonDamage(data, clientConfiguration, materialShortageDamageMap = {}) {
    // Check for seal no.
    if (Helper.isSealIntact(data.sealNoDetails)) {
      return {};
    }
    return Helper.getMaterialLevelSdlc(data, clientConfiguration, 'carton_damage_charge', materialShortageDamageMap);
  }
  cartonDamageUsingBaseFreight(data) {
    const freightCharge = data.overAllFreightRate;

    // Check for seal no.
    if (Helper.isSealIntact(data.sealNoDetails)) {
      return { cartonDamage: 0 };
    }
    const categoryMap = data.materials.category || {};
    const factor = data.defectsSettings.cartonDamage.factor;
    const tolerance = data.defectsSettings.cartonDamage.tolerance;
    const defectUnit = data.defectsSettings.cartonDamage.defectUnit;
    let totalMaterialCount = 0;
    const overAllCartonDamage = data.allDestinations.reduce((acc, destination) =>
      acc + destination.materials.reduce((total, material) => {
        const currentCategory = categoryMap[material.category];
        if (defectUnit === getShortageDamageUnits().MT.toLowerCase()) {
          if (currentCategory &&
            currentCategory.carton_damage_unit &&
            currentCategory.carton_damage_unit.toLowerCase() === defectUnit) {
            totalMaterialCount += this.getWeightInTon(material.weight);
            total += (material.epod.carton_damage_count || 0);
          }
        } else {
          totalMaterialCount += material.quantity.value;
          total += (material.epod.carton_damage_count || 0);
        }
        return total;
      }, 0), 0);
    const consigneeCartonDamage = data.destinations.reduce((acc, destination) =>
      acc + destination.paymentLevelMaterials.reduce((total, material) => {
        const currentCategory = categoryMap[material.category];
        if (defectUnit === getShortageDamageUnits().MT.toLowerCase()) {
          if (currentCategory &&
            currentCategory.carton_damage_unit &&
            currentCategory.carton_damage_unit.toLowerCase() === defectUnit) {
            total += (material.epod.carton_damage_count || 0);
          } else {
            // skip material
          }
        } else {
          total += (material.epod.carton_damage_count || 0);
        }
        return total;
      }, 0), 0);

    const cartonDamagePercent = (overAllCartonDamage * 100) / totalMaterialCount;
    const netCartonDamagePercent = Math.max(cartonDamagePercent - tolerance, 0);
    const netCartonDamage = ((netCartonDamagePercent * consigneeCartonDamage)
      / overAllCartonDamage) / 100;
    const cartonDamageCharge = netCartonDamage * factor * freightCharge;
    const calculatedCost = (Math.round((cartonDamageCharge + Number.EPSILON) * 100) / 100);
    const materialShortageDamageMap = {};
    data.destinations.forEach((destination) => {
      destination.paymentLevelMaterials.forEach((material) => {
        const locationId = destination.destinationGateId || destination.locationId;
        if (!materialShortageDamageMap[locationId]) {
          materialShortageDamageMap[locationId] = {};
        }
        const cost =
          ((material.epod.carton_damage_count || 0) / consigneeCartonDamage) * calculatedCost;
        materialShortageDamageMap[locationId][material._id] = cost;
      });
    });
    return { cartonDamage: calculatedCost, materialShortageDamageMap };
    /*
      round to 2 decimal place

      reason to add EPSILON
      no EPSILON - 1.005 => 1
      with EPSILON - 1.005 => 1.01
    */
  }
}
module.exports = CartonDamage;
